<template>
  <!-- Toggled with `opacity`, not `visibility` — `visibility: hidden` also drops these buttons
       from the Tab order, so a keyboard user could never focus them to reveal them. -->
  <span class="row-actions-slot">
    <span class="row-actions">
      <button
        v-if="perms.share"
        class="action-circle"
        v-tooltip.top="'Share'"
        aria-label="Share folder"
        @click.stop="$emit('share', folder)"
      >
        <i class="fas fa-share-alt"></i>
      </button>

      <button
        v-if="perms.rename"
        class="action-circle"
        v-tooltip.top="'Rename'"
        aria-label="Rename folder"
        @click.stop="rename"
      >
        <i class="fas fa-pen"></i>
      </button>

      <button
        v-if="perms.move"
        class="action-circle"
        v-tooltip.top="'Move'"
        aria-label="Move folder"
        @click.stop="$emit('move', folder)"
      >
        <i class="fas fa-arrows-alt"></i>
      </button>

      <button
        v-if="perms.pin"
        class="action-circle"
        :class="{ 'action-circle-active': folder.is_pinned }"
        v-tooltip.top="folder.is_pinned ? 'Unpin' : 'Pin to top'"
        :aria-label="folder.is_pinned ? 'Unpin folder' : 'Pin folder to top'"
        @click.stop="togglePin"
      >
        <i class="fas fa-thumbtack"></i>
      </button>

      <!-- A folder that only reached this tab by sharing is detached, not destroyed, so it must
           not look like a delete. -->
      <button
        v-if="perms.delete"
        class="action-circle action-circle-danger"
        v-tooltip.top="isUnshare ? 'Remove from this list' : 'Delete'"
        :aria-label="
          isUnshare ? 'Remove folder from this list' : 'Delete folder'
        "
        @click.stop="confirmDelete"
      >
        <i :class="isUnshare ? 'fas fa-unlink' : 'fas fa-trash-alt'"></i>
      </button>
    </span>
    <i class="fas fa-chevron-right row-chevron"></i>
  </span>
</template>

<script>
import Swal from "sweetalert2";
import { mapActions } from "vuex";
import { truncateMiddle } from "@/utils/fileHelpers.js";
import {
  confirmDialog,
  deleteFolderMessage,
  folderPermissions,
  promptRenameFolder,
} from "./useDocumentsArea";

/**
 * What you can do to a folder from its row. Shared by the top-level folder list and a folder's
 * own contents, so the two can never offer different actions for the same folder.
 */
export default {
  name: "FolderRowActions",
  props: {
    folder: { type: Object, required: true },
    subjectUserId: { type: Number, default: null },
  },
  emits: ["share", "move", "changed"],
  computed: {
    perms() {
      return folderPermissions(this, this.folder, this.subjectUserId);
    },

    /* At full length a 250-character name pushed the warning below the fold. */
    shortName() {
      return truncateMiddle(this.folder.name, 40);
    },

    /** Reaching this tab by sharing means delete detaches the share, not the folder. */
    isUnshare() {
      return !!this.folder.is_shared_with_subject;
    },

    isOwnList() {
      const me = this.$store.state.auth.user
        ? this.$store.state.auth.user.id
        : null;

      return !this.subjectUserId || Number(this.subjectUserId) === Number(me);
    },
  },
  methods: {
    ...mapActions({
      pinFolder: "folders/pin",
      unpinFolder: "folders/unpin",
      removeFolder: "folders/deleteFolder",
    }),

    rename() {
      promptRenameFolder(this, {
        folder: this.folder,
        onRenamed: () => this.$emit("changed"),
      });
    },

    togglePin() {
      const action = this.folder.is_pinned ? this.unpinFolder : this.pinFolder;

      action(this.folder.id)
        .then(() => this.$emit("changed"))
        .catch((err) => {
          if (!err || !err.accessDenied) {
            Swal.fire("Error", "Could not update the pin.", "error");
          }
        });
    },

    confirmDelete() {
      const dialog = confirmDialog(Swal);

      dialog
        .fire({
          title: this.isUnshare
            ? `Remove "${this.shortName}" from ${
                this.isOwnList ? "your" : "this"
              } list?`
            : `Delete "${this.shortName}"?`,
          text: deleteFolderMessage(this.folder, this.isOwnList),
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: this.isUnshare
            ? "Yes, remove it"
            : "Yes, delete it",
          cancelButtonText: "No, cancel",
          reverseButtons: true,
        })
        .then((result) => {
          if (!result.isConfirmed) {
            return;
          }

          this.removeFolder({
            folderId: this.folder.id,
            userId: this.subjectUserId,
          }).then((deleted) => {
            this.$emit("changed");
            // A failure already reported its own reason from the store.
            if (deleted) {
              dialog.fire(
                "Deleted!",
                "The folder has been deleted.",
                "success"
              );
            }
          });
        });
    },
  },
};
</script>

<style scoped>
/* The row that owns the hover state lives in the table, so the reveal is driven from there with
   :deep(). Here we only describe the buttons. */
.row-actions-slot {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  height: 30px;
  vertical-align: middle;
}

.row-chevron {
  /* Taken out of the flow so it cannot widen the slot; the actions define the width. */
  position: absolute;
  right: 0;
  color: var(--main-text-color);
  opacity: 0.35;
}
</style>
